import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPreview } from './MapPreview';
import { extractCoordinates, validateCoordinates, formatCoordinates, isShortUrl } from '@/utils/googleMapsParser';
import { MapPin, AlertCircle, CheckCircle } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';

/**
 * LocationDialog - Set GPS coordinates for a gym via Google Maps URL
 *
 * @param {boolean} open - Dialog open state
 * @param {function} onOpenChange - Dialog open state handler
 * @param {object} gym - Gym object with gym_id, name, and optionally google_maps_url/coordinates
 * @param {function} onSave - Callback after successful save (receives updated gym data)
 */
export function LocationDialog({ open, onOpenChange, gym, onSave }) {
  const [url, setUrl] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialize with existing gym data when dialog opens
  useEffect(() => {
    if (open && gym) {
      setUrl(gym.google_maps_url || '');
      setCoordinates(gym.coordinates || null);
      setError('');
    }
  }, [open, gym]);

  // Extract coordinates when URL changes
  useEffect(() => {
    if (!url) {
      setCoordinates(null);
      setError('');
      return;
    }

    // Check for short URLs (not supported)
    if (isShortUrl(url)) {
      setError('Short URLs (goo.gl, maps.app) are not supported. Please use "Copy Link" from Google Maps instead.');
      setCoordinates(null);
      return;
    }

    const coords = extractCoordinates(url);
    if (coords && validateCoordinates(coords)) {
      setCoordinates(coords);
      setError('');
    } else {
      setError('Could not extract coordinates from URL. Please paste a valid Google Maps link.');
      setCoordinates(null);
    }
  }, [url]);

  const handleSave = async () => {
    if (!coordinates) {
      toast.error('Please enter a valid Google Maps URL first');
      return;
    }

    setSaving(true);
    try {
      await axiosInstance.put(`/gyms/${gym.gym_id}/location`, {
        google_maps_url: url,
        coordinates: coordinates
      });

      toast.success(`Location saved for ${gym.name}`);

      if (onSave) {
        onSave({
          ...gym,
          google_maps_url: url,
          coordinates: coordinates
        });
      }

      onOpenChange(false);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to save location';
      toast.error(errorMsg);
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setUrl('');
    setCoordinates(null);
    setError('');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={20} className="text-maroon-600" />
            Set Location: {gym?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* URL Input */}
          <div className="space-y-2">
            <Label>Google Maps URL</Label>
            <Input
              placeholder="https://www.google.com/maps/@..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className={error ? 'border-red-300' : coordinates ? 'border-green-300' : ''}
            />
            <p className="text-xs text-gray-500">
              Open gym in Google Maps, click Share, then Copy Link
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Extracted Coordinates */}
          {coordinates && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <CheckCircle size={14} className="text-green-600" />
                Extracted Coordinates
              </Label>
              <div className="text-sm font-mono bg-green-50 text-green-800 p-3 rounded-lg border border-green-200">
                {formatCoordinates(coordinates)}
              </div>
            </div>
          )}

          {/* Map Preview */}
          <div className="space-y-2">
            <Label>Map Preview</Label>
            <MapPreview coordinates={coordinates} height={250} />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-2">
            {url && (
              <Button variant="ghost" onClick={handleClear} type="button">
                Clear
              </Button>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)} type="button">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!coordinates || saving}
              className="bg-maroon-600 hover:bg-maroon-700"
            >
              {saving ? 'Saving...' : 'Save Location'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default LocationDialog;
